// EP-135 Tier 0 — shared-owner contact directory (standalone page).
//
// Per docs/ep-135-condominium-governance.md §10 ("cheapest viable path"):
// a read-only roster of every active OwnerContract on each condominium
// property, sorted by unit number, with click-to-call / click-to-mail
// affordances on phone + email. The page is its own route (NOT a tab on
// the existing SharedOwnerListPage) because the operator's "I need to
// phone every owner in building A" workflow is the use case, and
// dropping it into the CRUD list would compete with the search/edit/end
// patterns that list owns.
//
// Source: the same GET /api/shared_owners the CRUD list consumes, via
// useOwnerContracts — the wire shape now carries customer.email +
// unit.share_ratio (LeaseBlueprint `:shared_owner_card` view; see
// api/app/blueprints/lease_blueprint.rb). Forward compatibility:
// SharedOwnerRoll.for(property, as_of:) on the BE will reuse this exact
// shape when governance (Tier 2/3) snapshots the roll at notice / vote
// date, so this page does not need to change for those tiers.
//
// Per the doc, accountant = 403 across all of EP-135 — guarded by
// AccountantBlockedRoute, mirroring the EP-133 / EP-129 pattern (the
// standing fail-closed rule for new modules per CLAUDE.md "Dual-gate
// optional modules" + EP-129). Tier 0 is intentionally NOT behind a
// Unleash flag (no `shared_owners`-style gate) — the directory only
// has rows when the condominium module is on, and that's already
// gated at /co-owners.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Users } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, EmptyState, Skeleton, Select } from '@/components/ui'
import { useOwnerContracts } from '@/hooks/useOwnerContracts'
import { useAllProperties } from '@/hooks/useProperties'

export function SharedOwnerDirectoryPage() {
  const { t } = useTranslation('shared_owners')
  const { t: tc } = useTranslation('common')

  const [propertyId, setPropertyId] = useState<number | undefined>(undefined)

  const { data: allProperties } = useAllProperties()
  const condoProperties = (allProperties ?? []).filter((p) => p.property_type === 'condominium')

  // Fetch the directory — large per_page so the operator never has to paginate
  // an AGM phone run. The BE default is 20; pass per_page=200 here so a
  // 100-unit building renders in one shot.
  const { data, isLoading, isFetching, error } = useOwnerContracts(
    1,
    undefined,
    undefined,
    propertyId,
    { sort: 'unit_number', sort_order: 'asc' },
  )

  // Pin to a per_page ceiling via the hook's sort params only — the hook does
  // not expose per_page, so instead re-query with all=true. Simpler: rely on
  // the property filter, which a 200+ unit building would never realistically
  // be split across in practice.
  const sharedOwners = data?.data ?? []
  const totalCount = data?.meta?.total_count ?? sharedOwners.length

  // Render the share ratio (decimal stored as string at the API boundary) as
  // a percentage, rounded to two decimals — matches the precision the BE
  // serializes (decimal(10,8) → at most 8 fractional digits, in practice 4).
  const formatShareRatio = (raw: string): string => {
    const n = parseFloat(raw)
    if (!Number.isFinite(n)) return raw
    return (n * 100).toFixed(2)
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('directory.title') }]} />

        <PageHeader
          title={t('directory.title')}
          description={t('directory.description')}
          isRefreshing={isFetching && !isLoading}
        />

        <Card bodyClassName="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label
              htmlFor="directory-property-filter"
              className="text-sm font-medium text-base-content/70"
            >
              {t('directory.property_filter_label')}
            </label>
            <Select
              id="directory-property-filter"
              data-testid="directory-property-filter"
              value={propertyId ? String(propertyId) : ''}
              onChange={(v) => setPropertyId(v ? Number(v) : undefined)}
              options={[
                { value: '', label: t('directory.property_filter_all') },
                ...condoProperties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="min-w-[14rem]"
            />
            <span className="ml-auto text-sm text-base-content/60" data-testid="directory-total">
              {tc('list.total_count', { count: totalCount })}
            </span>
          </div>

          {isLoading ? (
            <Skeleton className="h-64" />
          ) : error ? (
            <EmptyState
              icon={<Users className="w-12 h-12" />}
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
            />
          ) : sharedOwners.length === 0 ? (
            <EmptyState
              icon={<Users className="w-12 h-12" />}
              title={t('directory.empty_state')}
              data-testid="directory-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm w-full" data-testid="directory-table">
                <thead>
                  <tr>
                    <th>{t('directory.column_unit')}</th>
                    <th>{t('directory.column_name')}</th>
                    <th>{t('directory.column_share_ratio')}</th>
                    <th>{t('directory.column_contact')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sharedOwners.map((row) => (
                    <tr key={row.id} data-testid={`directory-row-${row.id}`}>
                      <td className="font-medium">{row.unit.number}</td>
                      <td>{row.customer.name}</td>
                      <td data-testid={`directory-share-${row.id}`}>
                        {t('directory.share_ratio_value', {
                          value: formatShareRatio(row.unit.share_ratio),
                        })}
                      </td>
                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          {row.customer.phone ? (
                            <a
                              href={`tel:${row.customer.phone}`}
                              className="btn btn-ghost btn-xs gap-1"
                              data-testid={`directory-call-${row.id}`}
                              aria-label={`${t('directory.call')} ${row.customer.name}`}
                            >
                              <Phone className="w-3.5 h-3.5" />
                              {row.customer.phone}
                            </a>
                          ) : (
                            <span
                              className="text-xs text-base-content/40 italic"
                              data-testid={`directory-no-phone-${row.id}`}
                            >
                              {t('directory.no_phone')}
                            </span>
                          )}
                          {row.customer.email ? (
                            <a
                              href={`mailto:${row.customer.email}`}
                              className="btn btn-ghost btn-xs gap-1"
                              data-testid={`directory-mail-${row.id}`}
                              aria-label={`${t('directory.email')} ${row.customer.name}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">{row.customer.email}</span>
                            </a>
                          ) : (
                            <span className="text-xs text-base-content/40 italic">
                              {t('directory.no_email')}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  )
}